import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, ArrowLeft, ArrowRight, Users, Wrench, Clock, CheckCircle, Loader2, Mail, User, Building2, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';


// Service details (no pricing displayed)
const SERVICES = {
  'expert-advice': {
    id: 'expert-advice',
    title: 'ISO Expert Advice',
    subtitle: 'One-on-One Sessions with Subject Matter Experts',
    description: 'Get personalized guidance from certified ISO 27001 experts. Our SMEs will help you understand your assessment results, prioritize remediation efforts, and create a roadmap for compliance.',
    icon: Users,
    duration: '60 minutes per session',
    features: [
      'One-on-one video consultation',
      'Assessment report review',
      'Prioritized action plan',
      'Implementation guidance',
      'Q&A with certified expert',
      'Session recording provided'
    ]
  },
  'iso-dfy': {
    id: 'iso-dfy',
    title: 'ISO Done-For-You',
    subtitle: 'Complete ISO 27001 Implementation Service',
    description: 'Let our experts handle your entire ISO 27001 implementation. From gap analysis to certification readiness, we manage the complete process so you can focus on your business.',
    icon: Wrench,
    duration: '8-12 weeks',
    features: [
      'Complete ISMS implementation',
      'All required documentation',
      'Policy & procedure development',
      'Risk assessment & treatment',
      'Staff awareness training',
      'Internal audit preparation',
      'Certification body liaison',
      'Post-certification support'
    ]
  }
};

const Services = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get('service') || 'expert-advice';
  const { user, isAuthenticated } = useAuth();
  
  const [selectedService, setSelectedService] = useState(initialService);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  const service = SERVICES[selectedService];
  const userPlan = user?.subscription?.plan || 'standard';

  // Pre-fill form with user data when logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        name: user.name || ''
      }));
    }
  }, [isAuthenticated, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      // Send notification to admin with customer details
      await axios.post(`${API_URL}/api/service-inquiry`, {
        ...formData,
        service_id: service.id,
        service_title: service.title,
        pack_type: userPlan === 'pro' ? 'Pro Pack' : 'Standard Pack'
      });
      
      setSubmitted(true);
      toast.success('Your inquiry has been submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Thank you screen after submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full text-center"
        >
          <div className="inline-flex items-center justify-center p-4 bg-green-100 rounded-full mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-slate-900 mb-3">
            Thank You!
          </h2>
          <p className="text-slate-600 mb-4">
            Thank you for expressing your interest in <strong>{service.title}</strong>.
          </p>
          <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-blue-800">
              Our concerned team will reach you soon to discuss your requirements and provide a customized quote.
            </p>
          </div>
          <p className="text-slate-500 text-sm mb-6">
            We typically respond within 24 business hours.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="w-full">
            Back to Dashboard
          </Button>
        </motion.div>
      </div>
    );
  }

  // Determine logo destination based on auth status
  const logoDestination = isAuthenticated && (userPlan === 'standard' || userPlan === 'pro') ? '/dashboard' : '/';
  const backButtonDestination = isAuthenticated && (userPlan === 'standard' || userPlan === 'pro') ? '/dashboard' : '/';
  const backButtonText = isAuthenticated && (userPlan === 'standard' || userPlan === 'pro') ? 'Back to Dashboard' : 'Back to Home';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to={logoDestination} className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-slate-800" />
              <span className="font-heading font-bold text-xl text-slate-900">AIVORA Comply360</span>
            </Link>
            <Button variant="outline" asChild>
              <Link to={backButtonDestination}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {backButtonText}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="font-heading font-bold text-4xl text-slate-900 mb-4">
            Expert Services
          </h1>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Accelerate your ISO 27001 journey with our professional services. Fill out the form and our team will reach out with a customized quote.
          </p>
        </div>

        {/* Service Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-white rounded-xl p-1.5 shadow-md border border-slate-200 inline-flex">
            <button
              onClick={() => { setSelectedService('expert-advice'); setSubmitted(false); }}
              className={`px-6 py-3 rounded-lg font-medium text-sm transition-all ${
                selectedService === 'expert-advice'
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Expert Advice
            </button>
            <button
              onClick={() => { setSelectedService('iso-dfy'); setSubmitted(false); }}
              className={`px-6 py-3 rounded-lg font-medium text-sm transition-all ${
                selectedService === 'iso-dfy'
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Wrench className="h-4 w-4 inline mr-2" />
              ISO Done-For-You
            </button>
          </div>
        </div>

        {/* Service Card */}
        <motion.div
          key={selectedService}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto"
        >
          {/* Left - Service Details */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-blue-100 rounded-xl">
                <service.icon className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-slate-900">{service.title}</h2>
                <p className="text-slate-500 text-sm">{service.subtitle}</p>
              </div>
            </div>

            <p className="text-slate-600 mb-6">{service.description}</p>

            <div className="flex items-center gap-4 mb-6 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {service.duration}
              </span>
            </div>

            <div className="border-t border-slate-100 pt-6">
              <h3 className="font-semibold text-slate-900 mb-4">What&apos;s Included:</h3>
              <ul className="space-y-3">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right - Inquiry Form */}
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center mb-6">
              <h3 className="font-heading font-bold text-xl mb-2">Express Your Interest</h3>
              <p className="text-slate-300 text-sm">
                Fill out the form below and our team will contact you with a customized quote
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    name="name"
                    placeholder="Full Name *"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400 ${errors.name ? 'border-red-400' : ''}`}
                  />
                </div>
                {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400 ${errors.email ? 'border-red-400' : ''}`}
                  />
                </div>
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    name="company"
                    placeholder="Company Name *"
                    value={formData.company}
                    onChange={handleInputChange}
                    className={`pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400 ${errors.company ? 'border-red-400' : ''}`}
                  />
                </div>
                {errors.company && <p className="text-red-400 text-xs mt-1">{errors.company}</p>}
              </div>

              <div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    name="phone"
                    placeholder="Phone Number *"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400 ${errors.phone ? 'border-red-400' : ''}`}
                  />
                </div>
                {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
              </div>

              <div>
                <textarea
                  name="message"
                  placeholder="Tell us about your requirements (optional)"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 text-white placeholder:text-slate-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <Button 
                type="submit"
                size="lg"
                className="w-full bg-white text-slate-900 hover:bg-slate-100 py-6 text-base font-semibold"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Submit Inquiry
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-slate-400 text-xs mt-4">
              Questions? Email us at <a href="mailto:shri@aivora-techlabs.com" className="text-blue-400 hover:underline">shri@aivora-techlabs.com</a>
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Services;
